'use client';

import { useId, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiAddLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { RiArrowRightUpLine } from '@/components/ui/rtl-icons';
import { Link } from '@/i18n/navigation';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * SSS — beyaz zeminde numaralı ölçüm listesi.
 *
 * Koyu bloğun (hero + marka şeridi) hemen altındaki ilk açık bölüm.
 *
 * Çizgi kuralı (kullanıcı kararı): DİKEY var, ÇAPRAZ yok. İçerik sütununu
 * saran 1px'lik hatlar hero'nunkilerin tam altına düşüyor — bölüm sayfanın
 * ızgarasına oturuyor. Yan boşluklardaki çapraz tarama ise sayfanın
 * tamamından kaldırıldı; buraya da eklenmeyecek. Bu yüzden yan sütunlar
 * (grid-item-2fr) hiç basılmıyor: taşıyacakları bir şey kalmadı.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Akordeon aynı anda tek soru açık tutuyor. Bir soru açılırken altındaki
 * satırların aşağı KAYDIĞI görülmeli — liste yeniden diziliyor, satırlar
 * yerinde ışınlanmıyor. Bunun için her satırda `layout` var; boyut ve konum
 * değişimini Motion transform'a çeviriyor (DESIGN.md §1: height/top asla
 * animasyonlanmaz).
 *
 * 🔑 Satırların GİRİŞ animasyonunda bilerek transform yok — yalnızca opaklık
 * ve blur. `layout` taşıyan bir öğede aynı anda y ötelemesi çalıştırmak,
 * Motion'ın ölçüm aldığı ana denk gelirse satırın sıçramasına yol açıyor.
 * Hareketi taşıyan şey zaten açılma/kapanma; giriş sadece "odaklanıyor".
 *
 * `AnimatePresence mode="popLayout"`: kapanan cevap DOM'dan çıkana kadar yer
 * kaplamasın diye akıştan koparılıyor. Olmasaydı satır ancak metin tamamen
 * söndükten sonra kısalır, kapanma iki aşamalı ve tembel görünürdü. Şimdi
 * metin sönerken kutu aynı anda kapanıyor.
 */

/** Sıra listede göründüğü sıra; numaralar (01…06) buradan türüyor. */
const QUESTIONS = ['platforms', 'control', 'setup', 'security', 'trial', 'agency'] as const;

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

/* Sol sütun tek parça girer — başlık bloğu bölünmez. */
const asideV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: 0.06 } },
};

/* Transform YOK — yukarıdaki nota bak. 6 satır blur bütçesinin (≈15) içinde. */
const rowV: Variants = {
  hidden: { opacity: 0, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    filter: 'blur(0px)',
    transition: { duration: dur.base, ease: ease.out },
  },
};

export function Faq() {
  const t = useTranslations('faq');
  const uid = useId();

  /* İlk soru açık: tamamen kapalı bir liste ilk bakışta içeriksiz duruyor ve
     satırların tıklanabilir olduğu okunmuyor. Tek açık kalem — ikinci soru
     açılınca ilki kapanır (aynı anda iki uzun cevap listeyi dağıtıyor). */
  const [openKey, setOpenKey] = useState<string | null>(QUESTIONS[0]);

  return (
    <section className="faq" aria-labelledby={`${uid}-title`}>
      {/* .grid-item-8fr zaten `grid-column: 2` ile açıkça yerleşiyor; boş
          kardeşlere ihtiyacı yok. Dikey hatlar bu sütunun kendi kenarları. */}
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <motion.div className="faq-inner" variants={sectionV} initial={false} animate="show">
            <div className="faq-layout">
              <motion.div className="faq-aside" variants={asideV}>
                <p className="faq-eyebrow">{t('eyebrow')}</p>
                <h2 className="faq-title" id={`${uid}-title`}>
                  {t('title')}
                </h2>
                <p className="faq-lead">{t('lead')}</p>

                <p className="faq-help">
                  <span className="faq-help-text">{t('helpLead')}</span>
                  <Link href="/help" className="faq-help-link">
                    {t('helpLink')}
                    <RiArrowRightUpLine className="faq-help-arrow" size={16} aria-hidden="true" />
                  </Link>
                </p>
              </motion.div>

              <motion.ul className="faq-list" variants={listV}>
                {QUESTIONS.map((key, i) => {
                  const open = openKey === key;
                  const panelId = `${uid}-${key}`;
                  const buttonId = `${uid}-${key}-btn`;

                  return (
                    <motion.li
                      key={key}
                      layout
                      transition={spring.default}
                      variants={rowV}
                      className={`faq-item${open ? ' faq-item--open' : ''}`}
                    >
                      <h3 className="faq-q-head">
                        {/* layout="position": satır büyürken Motion kutuyu
                            ölçekliyor; başlık kendi projeksiyon düğümü olmazsa
                            metin o süre boyunca dikeyde geriliyor. */}
                        <motion.button
                          layout="position"
                          type="button"
                          id={buttonId}
                          className="faq-q"
                          aria-expanded={open}
                          aria-controls={panelId}
                          onClick={() => setOpenKey(open ? null : key)}
                        >
                          <span className="faq-index" aria-hidden="true">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="faq-q-text">{t(`items.${key}.q`)}</span>
                          {/* + işareti 45° dönüp × oluyor. Azaltılmış hareket
                              tercihinde MotionConfig dönüşü anlık yapıyor —
                              durum yine de doğru okunuyor, üstelik açık/kapalı
                              bilgisi tek başına buna bağlı değil: cevabın
                              kendisi ve aria-expanded taşıyor (DESIGN.md §1). */}
                          <motion.span
                            className="faq-mark"
                            aria-hidden="true"
                            animate={{ rotate: open ? 45 : 0 }}
                            transition={spring.snap}
                          >
                            <RiAddLine size={20} />
                          </motion.span>
                        </motion.button>
                      </h3>

                      <AnimatePresence initial={false} mode="popLayout">
                        {open && (
                          <motion.div
                            key="answer"
                            layout="position"
                            id={panelId}
                            role="region"
                            aria-labelledby={buttonId}
                            className="faq-a"
                            initial={{ opacity: 0, filter: 'blur(4px)' }}
                            animate={{ opacity: 1, filter: 'blur(0px)' }}
                            exit={{ opacity: 0, filter: 'blur(3px)' }}
                            transition={{ duration: dur.fast, ease: ease.out }}
                          >
                            <p className="faq-a-text">{t(`items.${key}.a`)}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.li>
                  );
                })}
              </motion.ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
